import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import mktAccountsService from 'src/services/mktAccountsService';
import { INSTAGRAM_PROFILE_DESIGN as d } from './InstagramProfile.design';

function apiErrorMessage(err, fallback) {
    return err?.response?.data?.message
        || err?.message
        || fallback;
}

export default function InstagramProfile() {
    const { t } = useTranslation(['settings', 'common']);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const accountId = searchParams.get('accountId');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [profile, setProfile] = useState({
        name: '',
        username: '',
        biography: '',
        website: '',
        profile_picture_url: '',
    });

    const voltar = useCallback(() => {
        navigate('/dashboard/configuracoes/contas-sociais');
    }, [navigate]);

    const carregar = useCallback(async () => {
        if (!accountId) {
            setError(t('socialAccounts.profile.accountMissing'));
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await mktAccountsService.getInstagramProfile(accountId);
            if (res?.error) throw new Error(res.message || t('socialAccounts.profile.loadFailed'));
            setProfile(res.data?.profile || {});
        } catch (err) {
            setError(apiErrorMessage(err, t('socialAccounts.profile.metaError')));
        } finally {
            setLoading(false);
        }
    }, [accountId, t]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const igUrl = profile.username
        ? `https://www.instagram.com/${profile.username}/`
        : 'https://www.instagram.com/';

    const headerSubtitle = profile.username
        ? `@${profile.username}`
        : t('socialAccounts.instagram.editHint');

    const renderHeader = (actions = null) => (
        <div className={d.header}>
            <div className={d.headerInner}>
                <div className={d.topbar}>
                    <div className={d.topbarIdentity}>
                        <button type="button" className={d.backBtn} onClick={voltar} title={t('common:buttons.back')}>
                            <Iconify icon="eva:arrow-back-outline" width={18} />
                        </button>
                        <div className={d.iconTile}>
                            <Iconify icon="mdi:instagram" width={22} />
                        </div>
                        <div className="min-w-0">
                            <h1 className={d.title}>{t('socialAccounts.instagram.title')}</h1>
                            <p className={d.subtitle}>{headerSubtitle}</p>
                        </div>
                    </div>
                    {actions ? <div className={d.actions}>{actions}</div> : null}
                </div>
            </div>
        </div>
    );

    if (!accountId) {
        return (
            <div className={d.shell} style={{ fontFamily: d.fontStack }}>
                {renderHeader()}
                <div className={d.body}>
                    <div className={d.error}>
                        <Iconify icon="eva:alert-circle-outline" width={18} className="shrink-0 mt-0.5" />
                        <span>{t('socialAccounts.profile.accountMissingHint')}</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={d.shell} style={{ fontFamily: d.fontStack }}>
            {renderHeader(
                <>
                    <button
                        type="button"
                        className={d.actionOutline}
                        onClick={() => navigate('/dashboard/marketing/publicacoes')}
                    >
                        <Iconify icon="eva:paper-plane-outline" width={14} />
                        {t('socialAccounts.header.goToPosts')}
                    </button>
                    <button type="button" className={d.actionOutline} onClick={carregar} disabled={loading}>
                        <Iconify icon={loading ? 'eos-icons:loading' : 'eva:refresh-outline'} width={14} />
                        {t('socialAccounts.profile.reload')}
                    </button>
                    {!loading && (
                        <a
                            href={igUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={d.actionPrimary}
                        >
                            <Iconify icon="mdi:instagram" width={14} />
                            {t('socialAccounts.instagram.openInInstagram')}
                        </a>
                    )}
                </>
            )}

            <div className={d.body}>
                {error && (
                    <div className={d.error}>
                        <Iconify icon="eva:alert-circle-outline" width={18} className="shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}

                {loading ? (
                    <div className={d.loading}>
                        <Iconify icon="eos-icons:loading" width={28} className="text-pink-600" />
                        <span className="text-[13px] font-medium">{t('socialAccounts.profile.loading')}</span>
                    </div>
                ) : (
                    <div className={d.grid}>
                            <div className={d.stack}>
                                <section className={d.card}>
                                    <h2 className={d.cardTitle}>
                                        <span className={d.cardIcon}>
                                            <Iconify icon="eva:person-outline" width={16} />
                                        </span>
                                        {t('socialAccounts.instagram.title')}
                                    </h2>
                                    <div className={d.identityRow}>
                                        {profile.profile_picture_url ? (
                                            <img src={profile.profile_picture_url} alt="" className={d.avatar} />
                                        ) : (
                                            <div className={d.avatarFallback}>
                                                <Iconify icon="mdi:instagram" width={28} className="text-pink-600" />
                                            </div>
                                        )}
                                        <div className={d.identityMeta}>
                                            <p className={d.identityName}>{profile.name || '—'}</p>
                                            <p className={d.identityUser}>
                                                {profile.username ? `@${profile.username}` : '—'}
                                            </p>
                                            {profile.username && (
                                                <a
                                                    href={igUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={d.identityLink}
                                                >
                                                    <Iconify icon="eva:external-link-outline" width={13} />
                                                    instagram.com/{profile.username}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </section>

                                <section className={d.card}>
                                    <h2 className={d.cardTitle}>
                                        <span className={d.cardIcon}>
                                            <Iconify icon="eva:file-text-outline" width={16} />
                                        </span>
                                        {t('socialAccounts.instagram.biography')}
                                    </h2>
                                    <div className={d.formGrid}>
                                        <div className={d.field}>
                                            <label className={d.label}>{t('socialAccounts.instagram.name')}</label>
                                            <input
                                                type="text"
                                                readOnly
                                                value={profile.name || ''}
                                                className={d.input}
                                            />
                                        </div>
                                        <div className={d.field}>
                                            <label className={d.label}>{t('socialAccounts.instagram.username')}</label>
                                            <input
                                                type="text"
                                                readOnly
                                                value={profile.username ? `@${profile.username}` : ''}
                                                className={d.input}
                                            />
                                        </div>
                                        <div className={d.fieldFull}>
                                            <label className={d.label}>{t('socialAccounts.instagram.biography')}</label>
                                            <textarea
                                                readOnly
                                                value={profile.biography || ''}
                                                className={d.textarea}
                                                rows={4}
                                            />
                                        </div>
                                        <div className={d.fieldFull}>
                                            <label className={d.label}>{t('socialAccounts.instagram.website')}</label>
                                            <input
                                                type="text"
                                                readOnly
                                                value={profile.website || ''}
                                                className={d.input}
                                                placeholder="—"
                                            />
                                        </div>
                                    </div>
                                </section>

                                <div className={d.helpBox}>
                                    <Iconify icon="eva:info-outline" width={16} className="shrink-0 mt-0.5 text-amber-700" />
                                    <span>{t('socialAccounts.instagram.editHint')}</span>
                                </div>
                            </div>

                            <aside className={d.previewCard}>
                                <div className={d.previewHeader}>
                                    <Iconify icon="mdi:instagram" width={18} className="text-pink-600" />
                                    <span className={d.previewTitle}>{t('socialAccounts.instagram.previewTitle', 'Pré-visualização')}</span>
                                </div>
                                <div className={d.previewBody}>
                                    {profile.profile_picture_url ? (
                                        <img src={profile.profile_picture_url} alt="" className={d.previewAvatar} />
                                    ) : (
                                        <div className={d.previewAvatarFallback}>
                                            <Iconify icon="mdi:instagram" width={36} className="text-pink-600" />
                                        </div>
                                    )}
                                    <p className={d.previewName}>{profile.name || '—'}</p>
                                    <p className={d.previewUser}>
                                        {profile.username ? `@${profile.username}` : '—'}
                                    </p>
                                    {profile.biography ? (
                                        <p className={d.previewBio}>{profile.biography}</p>
                                    ) : (
                                        <p className={d.previewEmpty}>{t('socialAccounts.instagram.noBio', 'Sem biografia')}</p>
                                    )}
                                    {profile.website && (
                                        <a
                                            href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={d.previewWebsite}
                                        >
                                            {profile.website.replace(/^https?:\/\//, '')}
                                        </a>
                                    )}
                                </div>
                            </aside>
                    </div>
                )}
            </div>
        </div>
    );
}
